import { Menu, MenuItem } from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon } from '@atgfr8/icons';
import { BasicModal, Button } from '@components';
import { useParams } from '@solidjs/router';
import { openModal } from '@store/modals';
import { updateOrderState } from '@store/orders';
import { orderStore, setOrderStore } from '@store/orders/store';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { ADD_BILLING_HOLD_NOTE } from '@views/order/constants';
import { Component, createEffect, createSignal, For } from 'solid-js';

import BillingHoldModal from '../models/BillingHoldModal';
import { CopyMultipleModal, copyMultipleModalId } from './CopyMultipleModal';
import {
  SaveAsTemplateModal,
  saveAsTemplateModalId,
} from './SaveAsTemplateModal';
import { VoidModal, voidModalId } from './VoidModal';
import { getMenuItems } from './helpers';

type MoreOptionsProps = {
  onCopyOrder: () => Promise<void>;
};

const MoreOptions: Component<MoreOptionsProps> = (props) => {
  const params = useParams();
  const isCreate = !params.id;
  const [buttonRef, setButtonRef] = createSignal<HTMLButtonElement>();

  const [moreOptions, setMoreOptions] = createSignal(
    getMenuItems(orderStore.isReadOnly),
  );

  const handleOnClick = async (value: string) => {
    switch (value) {
      case 'Add Billing Hold/Note':
        openModal(ADD_BILLING_HOLD_NOTE);
        break;
      case 'Void Order':
        openModal(voidModalId);
        break;
      case 'Copy Multiple':
        openModal(copyMultipleModalId);
        break;
      case 'Save As Template':
        openModal(saveAsTemplateModalId);
        break;
      case 'Copy Order':
        await props.onCopyOrder();
        break;
      default:
        // Handle default case if necessary
        break;
    }
  };

  const newOrderMoreOptions = () => [
    { label: 'Copy Order', value: 'Copy Order' },
    { label: 'Copy Multiple', value: 'Copy Multiple' },
    { label: 'Save as Template', value: 'Save as Template' },
  ];

  createEffect(() => {
    if (isCreate) {
      setMoreOptions(newOrderMoreOptions);
      return;
    }
    setMoreOptions(() => {
      let items = getMenuItems(orderStore.isReadOnly);

      if (orderStore.order.status === 'Void') {
        items = items.filter((val) => val.label !== 'Void Order');
      }

      return items;
    });
  });

  return (
    <>
      <span ref={setButtonRef}>
        <Button
          endIcon={<MenuDownIcon size={20} />}
          label="More Options"
          size="sm"
          variant="outlined"
          class="h-[37px]! whitespace-nowrap"
          dataTestId={formatDataTestId('More Options', 'button')}
        />
      </span>
      <Menu anchorEl={buttonRef} class="w-[200px]">
        <For each={moreOptions()}>
          {(item) => (
            <MenuItem
              onClick={() => {
                void handleOnClick(item.value);
              }}
              class="w-[200px]"
            >
              {item.label}
            </MenuItem>
          )}
        </For>
      </Menu>

      <SaveAsTemplateModal />
      <BasicModal
        id={copyMultipleModalId}
        width="97vw"
        maxWidth="97vw"
        header={false}
        footer={false}
      >
        <CopyMultipleModal />
      </BasicModal>
      <BasicModal
        id={voidModalId}
        width="340px"
        header={false}
        footer={false}
        hideClose={true}
      >
        <VoidModal />
      </BasicModal>
      <BasicModal
        id={ADD_BILLING_HOLD_NOTE}
        width="900px"
        header={false}
        footer={false}
      >
        <BillingHoldModal
          modalId={ADD_BILLING_HOLD_NOTE}
          flags={orderStore.order.flags || []}
          updateFlags={(vals: FlagViewModel[] | Partial<IOrderViewModel>) => {
            if ('length' in vals && Array.isArray(vals)) {
              setOrderStore('order', 'flags', vals);
            } else {
              updateOrderState(vals);
            }
          }}
        />
      </BasicModal>
    </>
  );
};

export default MoreOptions;
